import palette from "assets/theme/base/palette";
import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Grid } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import YupPassword from "yup-password";
import { TextField, CircularProgress } from "@mui/material";
import { changePassword } from "services/awsService/awsService";
import * as Sentry from "@sentry/browser";
import { useTranslation } from "react-i18next";

YupPassword(yup);

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    bgcolor: "#fafafa",
  },
};

const ChangePassword = ({
  openChangePasswordDialog,
  setOpenChangePasswordDialog,
  setOpenSnackbar,
  setSeverity,
  setAlertMessage,
}) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword]           = useState(false);
  const [showNewPassword, setShowNewPassword]     = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading]                     = useState(false);
  const [newPasswordValue, setNewPasswordValue]   = useState("");

  const schema = yup.object().shape({
    password: yup.string().password()
      .min(8, t("alerts.password.passwordTooShort"))
      .minUppercase(1, t("alerts.password.passwordNoUppercase"))
      .minLowercase(1, t("alerts.password.passwordNoLowercase"))
      .minNumbers(1, t("alerts.password.passwordNoNumber"))
      .minSymbols(1, t("alerts.password.passwordNoSymbol"))
      .required(),
    newPassword: yup.string().password()
      .min(8, t("alerts.password.passwordTooShort"))
      .minUppercase(1, t("alerts.password.passwordNoUppercase"))
      .minLowercase(1, t("alerts.password.passwordNoLowercase"))
      .minNumbers(1, t("alerts.password.passwordNoNumber"))
      .minSymbols(1, t("alerts.password.passwordNoSymbol"))
      .required(),
    confirmPassword: yup.string()
      .oneOf([yup.ref("newPassword"), null], t("alerts.password.passwordNotMatch"))
      .required(),
  });

  const { handleSubmit, control, formState } = useForm({
    mode: "onChange",
    defaultValues: { password: "", newPassword: "", confirmPassword: "" },
    resolver: yupResolver(schema),
  });
  const { errors } = formState;

  // ── Password strength ──
  const getStrength = (pwd) => {
    let s = 0;
    if (pwd.length >= 8)          s++;
    if (/[A-Z]/.test(pwd))        s++;
    if (/[a-z]/.test(pwd))        s++;
    if (/[0-9]/.test(pwd))        s++;
    if (/[^A-Za-z0-9]/.test(pwd)) s++;
    return s;
  };
  const score = getStrength(newPasswordValue);
  const strengthLabel = ["", "Weak", "Weak", "Medium", "Strong", "Very Strong"][score] || "";
  const strengthColor = ["", "#f44336", "#f44336", "#ff9800", "#4caf50", "#2e7d32"][score] || "";

  const requirements = [
    { label: "At least 8 characters",  met: newPasswordValue.length >= 8 },
    { label: "1 number",                met: /[0-9]/.test(newPasswordValue) },
    { label: "1 uppercase letter",      met: /[A-Z]/.test(newPasswordValue) },
    { label: "1 special character",     met: /[^A-Za-z0-9]/.test(newPasswordValue) },
  ];

  const changePasswordHandler = (data) => {
    setLoading(true);
    changePassword(data.password, data.newPassword)
      .then(() => {
        setLoading(false);
        setOpenSnackbar(true);
        setSeverity("success");
        setAlertMessage(t("alerts.password.success"));
        setOpenChangePasswordDialog(false);
      })
      .catch((error) => {
        setLoading(false);
        setOpenSnackbar(true);
        setSeverity("error");
        if (error.message?.includes("Password does not match")) {
          setAlertMessage(t("alerts.password.passwordNotMatch"));
        } else if (error.message?.includes("Incorrect username or password")) {
          setAlertMessage(t("alerts.password.incorrect"));
        } else if (error.message?.includes("Attempt limit exceeded")) {
          setAlertMessage(t("alerts.password.limitExceeded"));
        } else {
          Sentry.captureException(error);
          setAlertMessage(t("alerts.password.fail"));
        }
      });
  };

  const onClose = () => setOpenChangePasswordDialog(false);

  return (
    <Dialog
      open={openChangePasswordDialog}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
    >
      {/* Close button */}
      <MDBox
        onClick={onClose}
        sx={{
          position: "absolute", top: 12, right: 12, zIndex: 10,
          width: 36, height: 36, borderRadius: "50%",
          bgcolor: palette.neutral[100], border: `1px solid ${palette.neutral[200]}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", transition: "all 180ms ease",
          "&:hover": { bgcolor: palette.neutral[200] },
        }}
      >
        <Icon sx={{ fontSize: "18px !important", color: palette.neutral[600] }}>close</Icon>
      </MDBox>

      <MDBox sx={{ px: 4, pt: 5, pb: 4 }}>
        {/* Header */}
        <MDBox textAlign="center" mb={3}>
          <MDBox
            sx={{
              width: 80, height: 80, borderRadius: "50%",
              bgcolor: palette.brand[50], display: "flex",
              alignItems: "center", justifyContent: "center", mx: "auto", mb: 2,
            }}
          >
            <Icon sx={{ fontSize: "40px !important", color: palette.brand[500] }}>lock</Icon>
          </MDBox>
          <MDTypography variant="h5" fontWeight="bold" mb={0.75}>
            {t("headings.changePassword")}
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, maxWidth: 380, mx: "auto" }}>
            Choose a strong password that you don&apos;t use for other websites and that&apos;s difficult to guess.
          </MDTypography>
        </MDBox>

        <form onSubmit={handleSubmit(changePasswordHandler)}>
          {/* Current Password */}
          <MDBox mb={2.5}>
            <MDTypography variant="body2" fontWeight="bold" mb={0.75}>
              {t("formLabels.password")} <span style={{ color: "#f44336" }}>*</span>
            </MDTypography>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth size="small"
                  placeholder={t("formPlaceHolders.currentPassword") || "Enter current password"}
                  type={showPassword ? "text" : "password"}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  {...field}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon sx={{ color: palette.neutral[400], fontSize: "20px !important" }}>lock_outline</Icon>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                          <Icon sx={{ fontSize: "20px !important" }}>{showPassword ? "visibility" : "visibility_off"}</Icon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
              )}
            />
          </MDBox>

          {/* New Password */}
          <MDBox mb={1.5}>
            <MDTypography variant="body2" fontWeight="bold" mb={0.75}>
              {t("formLabels.newPassword")} <span style={{ color: "#f44336" }}>*</span>
            </MDTypography>
            <Controller
              name="newPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth size="small"
                  placeholder={t("formPlaceHolders.newPassword") || "Enter new password"}
                  type={showNewPassword ? "text" : "password"}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword?.message}
                  {...field}
                  onChange={(e) => { field.onChange(e); setNewPasswordValue(e.target.value); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon sx={{ color: palette.neutral[400], fontSize: "20px !important" }}>lock_outline</Icon>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowNewPassword(!showNewPassword)} size="small">
                          <Icon sx={{ fontSize: "20px !important" }}>{showNewPassword ? "visibility" : "visibility_off"}</Icon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
              )}
            />
          </MDBox>

          {/* Password strength + requirements */}
          {newPasswordValue.length > 0 && (
            <MDBox mb={2.5}>
              <MDBox display="flex" alignItems="center" gap={1} mb={0.75}>
                <MDTypography variant="caption" color="text.secondary">Password strength:</MDTypography>
                <MDTypography variant="caption" fontWeight="bold" sx={{ color: strengthColor }}>
                  {strengthLabel}
                </MDTypography>
              </MDBox>
              <MDBox display="flex" gap={0.5} mb={1.5}>
                {[1, 2, 3, 4].map((i) => (
                  <MDBox
                    key={i}
                    sx={{
                      flex: 1, height: 4, borderRadius: "4px",
                      bgcolor: score >= i ? strengthColor : palette.neutral[200],
                      transition: "background-color 200ms",
                    }}
                  />
                ))}
              </MDBox>
              <Grid container rowSpacing={0.5} columnSpacing={2}>
                {requirements.map((req) => (
                  <Grid item xs={6} key={req.label}>
                    <MDBox display="flex" alignItems="center" gap={0.75}>
                      <Icon sx={{ fontSize: "14px !important", color: req.met ? "#4caf50" : palette.neutral[400] }}>
                        {req.met ? "check_circle" : "radio_button_unchecked"}
                      </Icon>
                      <MDTypography variant="caption" sx={{ color: req.met ? "#4caf50" : "text.secondary" }}>
                        {req.label}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                ))}
              </Grid>
            </MDBox>
          )}

          {/* Confirm Password */}
          <MDBox mb={3.5}>
            <MDTypography variant="body2" fontWeight="bold" mb={0.75}>
              {t("formLabels.confirmPassword")} <span style={{ color: "#f44336" }}>*</span>
            </MDTypography>
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth size="small"
                  placeholder={t("formPlaceHolders.confirmPassword") || "Confirm new password"}
                  type={showConfirmPassword ? "text" : "password"}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  {...field}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon sx={{ color: palette.neutral[400], fontSize: "20px !important" }}>lock_outline</Icon>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} size="small">
                          <Icon sx={{ fontSize: "20px !important" }}>{showConfirmPassword ? "visibility" : "visibility_off"}</Icon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
              )}
            />
          </MDBox>

          {/* Buttons */}
          <MDBox display="flex" gap={2}>
            <MDButton
              variant="outlined"
              fullWidth
              onClick={onClose}
              sx={{
                borderRadius: "12px", py: 1.5, textTransform: "none",
                borderColor: palette.brand[300], color: palette.brand[500],
                fontWeight: "bold",
                "&:hover": { bgcolor: `${palette.brand[500]}0a`, borderColor: palette.brand[500] },
              }}
            >
              {t("buttonTitles.cancel") || "Cancel"}
            </MDButton>
            <MDButton
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                borderRadius: "12px", py: 1.5, textTransform: "none",
                bgcolor: palette.brand[500], color: "#fff", fontWeight: "bold",
                "&:hover": { bgcolor: palette.brand[600] },
                "&.Mui-disabled": { bgcolor: palette.neutral[300], color: "#fff" },
              }}
            >
              {loading
                ? <CircularProgress size={20} color="inherit" />
                : t("buttonTitles.changePassword")
              }
            </MDButton>
          </MDBox>
        </form>
      </MDBox>
    </Dialog>
  );
};

export default ChangePassword;
